import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import NavigationTabs, { NavigationTab } from "components/NavigationTabs";
import Popover from "components/Popover";
import AskPaperTooltip from "components/Popover/AskPaperTooltip";
import { usePremiumFeatureModal } from "features/Subscription/components";
import { BetaTag } from "components/Tag/tags/BetaTag";
import { getPaperPdfUrl } from "helpers/api";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { useAppSelector } from "hooks/useStore";
import dynamic from "next/dynamic";
import { useMemo, useState } from "react";
import { CreditedFeature } from "store/slices/subscription";
import AskPaperThread from "./AskPaperThread";

// We must disable SSR to ensure the PDf worker works correctly
// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#configure-pdfjs-worker
const PDFViewer = dynamic(() => import("components/PDFViewer"), {
  ssr: false,
});

type AskPaperProps = {
  onClose: () => void;
  title: string;
  paperId: string;
};

export enum AskPaperTab {
  Chat = "Chat",
  FullText = "Full Text",
}

function AskPaper({ onClose, title, paperId }: AskPaperProps) {
  const isMobile = useAppSelector((state) => state.setting.isMobile);

  const [askPaperLabels] = useLabels("ask-paper");

  const [selectedExcerpt, setSelectedExcerpt] = useState("");

  const [selectedTab, setSelectedTab] = useState<AskPaperTab>(AskPaperTab.Chat);

  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [isPdfError, setIsPdfError] = useState(false);
  const [tooltipInstance, setTooltipInstance] = useState<any>(null);

  const handlePdfError = () => {
    setIsPdfLoading(false);
    setIsPdfError(true);
  };

  const paperPdfUrl = useMemo(() => {
    return getPaperPdfUrl(paperId);
  }, [paperId]);

  const { openPremiumFeatureModal } = usePremiumFeatureModal();

  const handleClickUpgradePremium = async () => {
    openPremiumFeatureModal({
      feature: CreditedFeature.ASK_PAPER,
      actionSource: MixpanelActionSource.AskPaper,
    });
    tooltipInstance?.hide();
  };

  const handleSelectExcerpt = (excerpt: string) => {
    setSelectedExcerpt(excerpt);
  };

  const tabs: NavigationTab[] = [
    {
      id: AskPaperTab.Chat,
      label: askPaperLabels["chat"],
      hidden: false,
      disabled: false,
    },
    {
      id: AskPaperTab.FullText,
      label: askPaperLabels["full-text"],
      hidden: false,
      disabled: false,
    },
  ];

  const handleTabClick = (tabId: string) => {
    setSelectedTab(tabId as AskPaperTab);
  };

  return (
    <div className="flex h-full">
      {!isMobile && (
        <div className="overflow-hidden flex-1">
          <PDFViewer
            url={paperPdfUrl}
            title={title}
            highlight={selectedExcerpt}
            onLoadSuccess={() => setIsPdfLoading(false)}
            onLoadError={handlePdfError}
          />
        </div>
      )}
      <div className="flex flex-col h-full pt-3 overflow-hidden w-full shrink-0 md:w-[375px] lg:w-[500px] md:border-l">
        <div className="md:border-b">
          <div className="flex pl-4 md:pb-3 pr-1.5">
            <div className="flex flex-1 gap-2 items-center">
              <i className="text-2xl icon-file-search" />
              <h2 className="lg-bold">{askPaperLabels["ask-paper"]}</h2>
              <Popover
                interactive
                maxWidth={350}
                onShown={(instance: any) => {
                  setTooltipInstance(instance);
                }}
                onHidden={(instance: any) => {
                  setTooltipInstance(instance);
                }}
                placement="bottom"
                tooltipContent={
                  <AskPaperTooltip
                    onClickUpgradeToPremium={handleClickUpgradePremium}
                  />
                }
              >
                <BetaTag />
              </Popover>
            </div>
            <CoButton
              variant={ButtonVariant.Flat}
              size="sm"
              onClick={onClose}
              icon="icon-x"
              data-testid="close-button"
            />
          </div>
          <NavigationTabs
            tabs={tabs}
            selectedTab={selectedTab}
            onTabClick={handleTabClick}
            className="md:hidden"
          />
        </div>
        {isMobile && (
          <div
            className={classNames(
              "flex-1 overflow-auto",
              selectedTab === AskPaperTab.Chat ? "hidden" : ""
            )}
          >
            <PDFViewer
              url={paperPdfUrl}
              title={title}
              highlight={selectedExcerpt}
              onLoadSuccess={() => setIsPdfLoading(false)}
              onLoadError={handlePdfError}
            />
          </div>
        )}
        <div
          className={classNames(
            "flex-1 overflow-auto",
            selectedTab === AskPaperTab.FullText ? "hidden md:block" : ""
          )}
        >
          <AskPaperThread
            isPdfError={isPdfError}
            isPdfLoading={isPdfLoading}
            onClose={onClose}
            onSelectExcerpt={handleSelectExcerpt}
            paperId={paperId}
            onClickUpgradeToPremium={handleClickUpgradePremium}
          />
        </div>
      </div>
    </div>
  );
}

export default AskPaper;
